import type React from 'react'
import { type RefObject, useEffect, useRef, useState } from 'react'

function isRefObject<T>(
  ref: React.RefObject<T> | React.ForwardedRef<T>,
): ref is React.RefObject<T> {
  return !!ref && typeof ref !== 'function'
}

export const useIntersectionObserver = <T extends HTMLElement>({
  enterThreshold = 1,
  freezeOnceVisible = false,
}): [RefObject<T | null>, boolean] => {
  const targetRef = useRef<T>(null)
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    const target = isRefObject(targetRef) && targetRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio <= 0) {
          // Exit
          setIsIntersecting(entry.isIntersecting)
        } else if (entry.intersectionRatio >= enterThreshold) {
          // Enter
          setIsIntersecting(entry.isIntersecting)

          if (freezeOnceVisible && entry.isIntersecting) {
            observer.disconnect()
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, enterThreshold],
      },
    )

    observer.observe(target)

    return () => {
      if (target) {
        observer.unobserve(target)
      }
      observer.disconnect()
    }
  }, [enterThreshold, freezeOnceVisible])

  return [targetRef, isIntersecting]
}
